import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { JuryShell } from './JuryDashboard';
import { formatApiErrorDetail } from '@/context/JuryAuthContext';
import { CheckCircle2, Circle, FileText, ArrowRight, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

const STATUS_BADGE = {
  pending: { label: 'Pending', color: 'var(--ink-muted)', Icon: Circle },
  draft: { label: 'Saved Draft', color: '#E0A659', Icon: Circle },
  submitted: { label: 'Evaluated', color: '#7BC47F', Icon: CheckCircle2 },
};

const JuryEntries = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await ax.get(`${API}/jury/entries`);
      setEntries(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Could not load entries.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let out = entries;
    if (filter !== 'all') out = out.filter((e) => (e.score_status || 'pending') === filter);
    if (q.trim()) {
      const s = q.toLowerCase();
      out = out.filter((e) =>
        [e.entry_title, e.brand_name, e.category].filter(Boolean).some((v) => String(v).toLowerCase().includes(s))
      );
    }
    return out;
  }, [entries, q, filter]);

  return (
    <JuryShell active="entries">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-12" data-testid="jury-entries-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-[10px] tracking-[0.5em] uppercase text-[var(--gold)] mb-3">Evaluations</div>
            <h1 className="font-[Libre_Baskerville] text-3xl md:text-4xl text-[var(--ink)]">Assigned Entries</h1>
            <p className="text-[var(--ink-muted)] text-sm mt-2">Review and score each entry. All identifying information has been removed.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-muted)]" />
              <input data-testid="jury-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="bg-[var(--bg-alt)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none pl-9 pr-3 py-2 text-[var(--ink)] text-sm w-56" />
            </div>
            <select data-testid="jury-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-[var(--bg-alt)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 text-[var(--ink)] text-sm" style={{ colorScheme: 'dark' }}>
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="draft">Drafts</option>
              <option value="submitted">Evaluated</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center text-[var(--ink-muted)] tracking-[0.3em] uppercase text-xs" data-testid="jury-entries-loading">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-20 border border-dashed border-[var(--gold-faint)] text-center text-[var(--ink-muted)] text-sm" data-testid="jury-entries-empty">
            {entries.length === 0 ? 'No entries assigned to your panel yet.' : 'No entries match your search.'}
          </div>
        ) : (
          <div className="border border-[var(--gold-faint)] divide-y divide-[var(--gold-faint)]/40" data-testid="jury-entries-list">
            {filtered.map((e, i) => {
              const meta = STATUS_BADGE[e.score_status || 'pending'] || STATUS_BADGE.pending;
              const Icon = meta.Icon;
              return (
                <Link key={e.id} to={`/jury-chamber/entry/${e.id}`} data-testid={`jury-entry-row-${i}`} className="block p-5 md:p-6 hover:bg-[var(--gold)]/5 transition-colors group">
                  <div className="flex flex-wrap items-start gap-5">
                    <div className="flex-1 min-w-[260px]">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon size={14} style={{ color: meta.color }} />
                        <span className="text-[10px] tracking-[0.3em] uppercase" style={{ color: meta.color }}>{meta.label}</span>
                        {e.total != null && <span className="text-[10px] tracking-[0.3em] uppercase text-[var(--gold)]">· Score {e.total}/100</span>}
                      </div>
                      <h3 className="font-[Libre_Baskerville] text-xl text-[var(--ink)] leading-tight group-hover:text-[var(--gold)] transition-colors">{e.entry_title || <span className="italic text-[var(--ink-muted)]">Untitled entry</span>}</h3>
                      <div className="mt-2 text-xs text-[var(--ink-muted)] space-x-3">
                        {e.brand_name && <span>Brand: <span className="text-[var(--ink)]/80">{e.brand_name}</span></span>}
                        {e.category && <span>· <span className="text-[var(--ink)]/80">{e.category}</span></span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-[var(--gold)] text-[10px] tracking-[0.3em] uppercase">
                      Open <ArrowRight size={12} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </JuryShell>
  );
};

export default JuryEntries;
